import { useEffect, useMemo, useState } from 'react';
import { CLIENT_USER_ROLE } from '@api/types/client-user-management';
import { UserAccess } from '@api/types/client-user-management';
import { buildDashboardSelectOptions } from '../utils';

type UseUserAccessFormStateOptions = {
  open: boolean;
  initialRole?: CLIENT_USER_ROLE;
  initialDashboardIds?: string[];
  dashboardsList: UserAccess[];
};

export const useUserAccessFormState = ({
  open,
  initialRole,
  initialDashboardIds,
  dashboardsList,
}: UseUserAccessFormStateOptions) => {
  const [selectedRole, setSelectedRole] = useState(CLIENT_USER_ROLE.OWNER);
  const [selectedDashboardIds, setSelectedDashboardIds] = useState<string[]>([]);
  const [dashboardAccordionExpanded, setDashboardAccordionExpanded] = useState(true);

  useEffect(() => {
    if (!open) {
      setSelectedRole(CLIENT_USER_ROLE.OWNER);
      setSelectedDashboardIds([]);
      setDashboardAccordionExpanded(false);
      return;
    }

    if (initialRole) setSelectedRole(initialRole);

    if (initialDashboardIds) setSelectedDashboardIds(initialDashboardIds);

    setDashboardAccordionExpanded(true);
  }, [open, initialRole, initialDashboardIds]);

  const dashboardOptions = useMemo(() => {
    return buildDashboardSelectOptions(selectedRole, dashboardsList);
  }, [selectedRole, dashboardsList]);

  useEffect(() => {
    const validIds = new Set(dashboardOptions.map((option) => option.value));
    setSelectedDashboardIds((previous) => {
      const filtered = previous.filter((id) => validIds.has(id));
      return filtered.length === previous.length ? previous : filtered;
    });
  }, [dashboardOptions]);

  const handleDashboardToggle = (dashboardId: string) => {
    setSelectedDashboardIds((previous) =>
      previous.includes(dashboardId)
        ? previous.filter((dashboard) => dashboard !== dashboardId)
        : [...previous, dashboardId],
    );
  };

  const selectAllDashboards = () => {
    setSelectedDashboardIds(dashboardOptions.map((option) => option.value));
  };

  const clearAllDashboards = () => {
    setSelectedDashboardIds([]);
  };

  return {
    selectedRole,
    setSelectedRole,
    selectedDashboardIds,
    dashboardOptions,
    dashboardAccordionExpanded,
    setDashboardAccordionExpanded,
    handleDashboardToggle,
    selectAllDashboards,
    clearAllDashboards,
  };
};
